/**
 * Tasks Data Hook
 *
 * Provides task data for both Demo and Live modes.
 * In Live mode, fetches from the Tasks API.
 * In Demo mode, returns mock data from the existing useTaskBoard hook.
 */

import { useQueryClient } from '@tanstack/react-query';
import type {
  CreateTaskApiRequest,
  GetTasksApiResponse,
  TaskListItemApi,
  TasksApiQueryParams,
  TaskTypeLookupApi,
  UpdateTaskApiRequest,
} from '@/types/api/tasks';
import { mockTaskTypes } from '@/lib/demo-data/tasks';
import {
  completeTaskDemoResponse,
  createTaskDemoResponse,
  updateTaskDemoResponse,
} from '@/services/demo/tasks-demo.service';
import type { TaskPriority, TaskStatus, UnifiedTask } from '@/types/tasks';
import { useApi } from './useApi';
import { useModeAwareMutation, useModeAwareQuery } from './useModeAwareQuery';

/**
 * Build the deep-link URL for the source record linked to a task.
 * Falls back to highlighting the task on the tasks page when there is no link.
 */
function buildSourceUrl(item: TaskListItemApi): string {
  if (item.linkId == null) {
    return `/tasks?highlight=${item.taskId}`;
  }

  const typeName = (item.taskTypeName ?? '').toLowerCase();

  if (typeName.includes('phone') && item.linkType) {
    switch (item.linkType) {
      case 'Promise':
        return `/collections/promises/${item.linkId}`;
      case 'Case':
        return `/collections/queue/case/${item.linkId}`;
      case 'Dispute':
        return `/disputes/${item.linkId}`;
      default:
        return `/tasks?highlight=${item.taskId}`;
    }
  }

  if (typeName.includes('promise')) {
    return `/collections/promises/${item.linkId}`;
  }
  if (typeName.includes('collection') || typeName.includes('case')) {
    return `/collections/queue/case/${item.linkId}`;
  }
  if (typeName.includes('phone')) {
    return `/tasks?highlight=${item.taskId}`;
  }
  if (typeName.includes('dispute')) {
    return `/disputes/${item.linkId}`;
  }

  return `/tasks?highlight=${item.taskId}`;
}

/**
 * Map backend TaskType codes to frontend TaskType values.
 * Backend codes come from the TaskTypes table (e.g. "Disputes", "Collections").
 */
function mapApiTaskTypeToFrontend(apiCode: string | null | undefined): UnifiedTask['type'] {
  switch (apiCode) {
    case 'Collections':
      return 'collection_case';
    case 'Promises':
      return 'promise';
    case 'Disputes':
      return 'dispute';
    case 'PhoneCall':
      return 'task';
    case 'Task':
      return 'task';
    default:
      return 'task';
  }
}

/**
 * Map frontend TaskType values to backend TaskType codes for API filtering.
 */
export function mapFrontendTaskTypeToApi(frontendType: string): string {
  switch (frontendType) {
    case 'collection_case':
      return 'Collections';
    case 'promise':
      return 'Promises';
    case 'dispute':
      return 'Disputes';
    case 'transaction':
      return 'Transaction';
    case 'task':
      return 'Task';
    default:
      return frontendType;
  }
}

/**
 * Map a single API task item to the frontend UnifiedTask model
 */
function mapApiTaskToUnified(item: TaskListItemApi): UnifiedTask {
  return {
    id: String(item.taskId),
    type: mapApiTaskTypeToFrontend(item.taskType),
    title: item.title,
    description: item.description ?? '',
    priority: (item.priority ?? 'Medium') as TaskPriority,
    status: (item.status?.toLowerCase() === 'completed' ? 'completed' : 'open') as TaskStatus,
    dueDate: new Date(item.dueDate),
    assignedTo: item.assignedUserName ?? 'Unassigned',
    assignedToId: String(item.userAssignedId),
    customerId: item.customerId != null ? String(item.customerId) : '',
    customerName: item.customerName ?? '',
    customerAccountNumber: item.customerAccountNumber ?? '',
    amount: undefined,
    sourceType: item.taskTypeName ?? 'Task',
    sourceId: item.linkId != null ? String(item.linkId) : String(item.taskId),
    sourceUrl: buildSourceUrl(item),
    linkType: item.linkType ?? undefined,
    createdAt: new Date(item.createdAt),
    updatedAt: new Date(item.updatedAt),
    tags: [item.taskTypeName ?? 'Task', item.statusName ?? ''],
  };
}

/**
 * Hook to fetch tasks list from the API
 * In demo mode returns empty array (the page will use useTaskBoard instead)
 */
export function useTasksList(params?: TasksApiQueryParams) {
  const { apiGet } = useApi();

  return useModeAwareQuery<UnifiedTask[], Error>(
    ['tasks-list', params],
    async ({ signal }) => {
      const response = await apiGet<GetTasksApiResponse>('/api/v1/tasks', params, signal);
      return response.data.map(mapApiTaskToUnified);
    },
    [], // Demo mode returns empty - the page uses useTaskBoard for demo
    {
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}

/**
 * Hook to fetch task type lookup options from the API
 */
export function useTaskTypeLookups() {
  const { apiGet } = useApi();

  return useModeAwareQuery<TaskTypeLookupApi[], Error>(
    ['task-type-lookups'],
    async ({ signal }) => {
      return apiGet<TaskTypeLookupApi[]>('/api/v1/tasks/types', undefined, signal);
    },
    mockTaskTypes,
    { staleTime: 60 * 1000, refetchOnWindowFocus: false },
  );
}

/**
 * Hook to create a new task via the API.
 * In demo mode returns a mock response. On success invalidates the tasks list.
 */
export function useCreateTaskMutation() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<TaskListItemApi, Error, CreateTaskApiRequest>(
    async (data) => {
      return apiPost<TaskListItemApi>('/api/v1/tasks', data);
    },
    createTaskDemoResponse,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['tasks-list'] });
      },
    },
  );
}

/**
 * Hook to update an existing task via the API.
 * In demo mode returns a mock response. On success invalidates the tasks list.
 */
export function useUpdateTaskMutation() {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<TaskListItemApi, Error, { taskId: number; data: UpdateTaskApiRequest }>(
    async ({ taskId, data }) => {
      return apiPut<TaskListItemApi>(`/api/v1/tasks/${taskId}`, data);
    },
    updateTaskDemoResponse,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['tasks-list'] });
      },
    },
  );
}

/**
 * Hook to complete a task via the API.
 * In demo mode returns a mock response. On success invalidates the tasks list.
 */
export function useCompleteTaskMutation() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<TaskListItemApi, Error, number>(
    async (taskId) => {
      return apiPost<TaskListItemApi>(`/api/v1/tasks/${taskId}/complete`);
    },
    completeTaskDemoResponse,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['tasks-list'] });
      },
    },
  );
}
